/**
 * Generic taskboard item client for api-devtools `/taskboard/v1/*`
 * (items, boards search, comments). EPIC/ITEM create/list and comments live
 * here; plan reparent stays on `/devtools/v1/plan/{id}/parent/update`.
 */
import type { TaskboardComment, TaskboardItem } from '../shared/types';
import { getTaskboardApiUrl } from '../utils/env';
import { getTokenFromStorage } from '../utils/tokenManager';
import { withRetry } from '../utils/withRetry';

/**
 * Authenticated JSON fetch with retry against api-devtools `/taskboard`.
 * Attaches Bearer token; surfaces HTTP status on Error for UI toasts.
 */
async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getTokenFromStorage();
  const exec = async () => {
    const res = await fetch(`${getTaskboardApiUrl()}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
    if (!res.ok) {
      // Prefer API error/message/detail fields so UI toasts stay readable.
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
        detail?: string;
      };
      const err = new Error(
        body.error || body.message || body.detail || `HTTP ${res.status}`,
      ) as Error & { status: number };
      err.status = res.status;
      throw err;
    }
    if (res.status === 204) {
      return undefined as T;
    }
    return res.json() as Promise<T>;
  };
  return withRetry(exec);
}

export type ItemsSearchResponse = {
  items: TaskboardItem[];
  count_total: number;
  has_more: boolean;
};

/** Mutating + search API for generic taskboard cards (epics, reparent). */
export const taskboardApi = {
  search: (body: Record<string, unknown>) =>
    request<ItemsSearchResponse>('/v1/items/search', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  create: (body: Record<string, unknown>) =>
    request<TaskboardItem>('/v1/item/create', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (itemId: string, body: Record<string, unknown>) =>
    request<TaskboardItem>(`/v1/item/${itemId}/update`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  get: (itemId: string) => request<TaskboardItem>(`/v1/item/${itemId}`),

  /**
   * List non-archived EPIC roots on the Coding Agent PRs board.
   * teamId is required: admin JWTs are rejected without an explicit team.
   */
  listEpics: (boardId: string, teamId: string) =>
    taskboardApi.search({
      board_id: boardId,
      team_id: teamId,
      item_type: 'EPIC',
      roots_only: true,
      page_size: 200,
    }),

  /**
   * Create an EPIC root card on the Coding Agent PRs board.
   * teamId is required: admin JWTs are rejected without an explicit team.
   */
  createEpic: (boardId: string, teamId: string, title: string) =>
    taskboardApi.create({
      board_id: boardId,
      team_id: teamId,
      title,
      item_type: 'EPIC',
    }),

  /**
   * Create a human ITEM ticket (appears in the Staff swimlane).
   * teamId is required: admin JWTs are rejected without an explicit team.
   */
  createTicket: (boardId: string, teamId: string, title: string, parentId?: string | null) =>
    taskboardApi.create({
      board_id: boardId,
      team_id: teamId,
      title,
      item_type: 'ITEM',
      ...(parentId ? { parent_id: parentId } : {}),
    }),

  /**
   * List boards with custom.kind=pr_tracker for the multi-board switcher.
   * teamId is required: taskboard require_explicit_team rejects bare admin JWTs.
   */
  listTrackerBoards: (teamId: string) =>
    request<{
      boards: Array<{
        board_id: string;
        title: string;
        project_id?: string | null;
        custom?: Record<string, unknown>;
      }>;
    }>('/v1/boards/search', {
      method: 'POST',
      body: JSON.stringify({ team_id: teamId, custom_kind: 'pr_tracker', page_size: 100 }),
    }),

  /** Newest-first comments for a taskboard item (plan or ticket). */
  listComments: (itemId: string) =>
    request<{ comments: TaskboardComment[]; count_total: number; has_more: boolean }>(
      `/v1/item/${itemId}/comments/search`,
      {
        method: 'POST',
        body: JSON.stringify({ page_size: 100 }),
      },
    ),

  /** Post a comment; author comes from the Bearer JWT on api-devtools taskboard. */
  createComment: (itemId: string, body: string) =>
    request<TaskboardComment>(`/v1/item/${itemId}/comment/create`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
};
